/**
 * EL CANDADO DE UN FRENTE.
 *
 * Esteban puso `conCandado` como casilla de cada frente: él decide a qué le
 * pone llave, y tener llave no hace que un frente valga menos.
 *
 * ═══ QUÉ PROTEGE, Y QUÉ NO ═══
 *
 * Protege la PANTALLA, no los datos. Alguien con la sesión abierta y la
 * consola del navegador puede pedir `/api/chats` y ver todo: el servidor no
 * sabe si el dueño desbloqueó o no, y hacerlo saber pediría una segunda sesión
 * con su propio vencimiento.
 *
 * Eso está bien para lo que es —que el primo que te agarra el teléfono no
 * entre a Familia— y está MAL para esconder algo de alguien que sabe usar un
 * navegador. Se dice en la pantalla de frentes: prometer más sería peor que no
 * tener candado.
 *
 * ═══ POR QUÉ HUELLA *Y* PIN ═══
 *
 * WebAuthn no existe en todos los navegadores ni en todos los equipos, y un
 * candado que a veces no se puede abrir es un candado que se apaga. El PIN es
 * el piso; la huella, el atajo cuando el equipo la tiene.
 *
 * El PIN se guarda HASHEADO en el navegador. No es criptografía seria —quien
 * pueda leer el localStorage puede probar cuatro mil combinaciones—, pero
 * guardarlo en claro haría que abrir la consola bastara para leerlo.
 */

const CLAVE_PIN = 'athena.candado.pin';
const CLAVE_ABIERTO = 'athena.candado.abierto';
/** Se cierra solo a los dos minutos, como pedía el mandato. */
const MINUTOS = 2;

async function hashear(pin: string): Promise<string> {
  const datos = new TextEncoder().encode(`athena:${pin}`);
  const buf = await crypto.subtle.digest('SHA-256', datos);
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** ¿Ya hay un PIN puesto? */
export function hayPin(): boolean {
  try {
    return Boolean(localStorage.getItem(CLAVE_PIN));
  } catch {
    // Ventana privada o almacenamiento bloqueado: sin PIN guardado, el candado
    // pide ponerlo. Fallar cerrado.
    return false;
  }
}

export async function ponerPin(pin: string): Promise<void> {
  if (!/^\d{6}$/.test(pin)) throw new Error('El PIN son 6 números');
  localStorage.setItem(CLAVE_PIN, await hashear(pin));
}

export async function pinCorrecto(pin: string): Promise<boolean> {
  try {
    const guardado = localStorage.getItem(CLAVE_PIN);
    if (!guardado) return false;
    return guardado === (await hashear(pin));
  } catch {
    return false;
  }
}

/** Marca el candado como abierto, por dos minutos. */
export function abrir(): void {
  try {
    localStorage.setItem(CLAVE_ABIERTO, String(Date.now() + MINUTOS * 60_000));
  } catch {
    /* sin almacenamiento, el candado pide la llave cada vez: es lo seguro */
  }
}

export function cerrar(): void {
  try {
    localStorage.removeItem(CLAVE_ABIERTO);
  } catch {
    /* nada que cerrar */
  }
}

/** ¿Está abierto ahora? Vencido cuenta como cerrado. */
export function estaAbierto(): boolean {
  try {
    const hasta = Number(localStorage.getItem(CLAVE_ABIERTO) ?? 0);
    if (!hasta || Date.now() > hasta) {
      // Se limpia al pasar: dejar la marca vencida haría que un reloj
      // cambiado la reviviera.
      localStorage.removeItem(CLAVE_ABIERTO);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** ¿Este equipo tiene huella o cara? */
export async function hayHuella(): Promise<boolean> {
  try {
    if (!window.PublicKeyCredential) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Pide la huella. `true` si el equipo verificó a la persona.
 *
 * Usa `navigator.credentials.get` con un reto al azar y sin credenciales
 * declaradas: no estamos autenticando contra un servidor, estamos pidiéndole
 * al equipo que confirme que es su dueño. Para un candado de pantalla alcanza,
 * y no obliga a registrar una credencial antes de poder usarlo.
 */
export async function pedirHuella(): Promise<boolean> {
  try {
    if (!(await hayHuella())) return false;
    const reto = crypto.getRandomValues(new Uint8Array(32));
    const cred = await navigator.credentials.get({
      publicKey: {
        challenge: reto,
        timeout: 30_000,
        userVerification: 'required',
        rpId: window.location.hostname,
      },
    });
    return Boolean(cred);
  } catch {
    // Cancelar cuenta como no abrir. No se cae a "pasa igual": un candado que
    // se abre cuando falla no es un candado.
    return false;
  }
}
